/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectors, hooks } from 'farmx-redux-core';
import { CloseOutlined } from '@ant-design/icons';
//@ts-ignore
import { BlockSelect, RanchSelect } from 'farmx-web-ui';
import {
  Form,
  Radio,
  Space,
  Tag
} from "antd";
import {
  useRenderReusableControls, useUpdateGlobalSettingsFormFields, useHandlers
} from 'helper/reusableControls';
import { isMobile } from 'utils/detectDevice';
import { FORM_RULES, capabilities } from '../../../constants';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import SensorControl from './SensorControl';
import './ruleSettingsPage.less';

interface GlobalSettingsConfigProps {
  isEdit?: boolean;
  renderItems?: string[];
  settings?: any;
  onUpdate?: any;
  formRef?: any;
  onCancel?: any;
  loading?: boolean;
}

const { useBlockNames, useRanchNames, useBlockIdsForRanchIds } = hooks;

const { selectUserPressureFormat } = selectors;

const GlobalSettingsConfig: React.FC<GlobalSettingsConfigProps> = (props: any) => {
  const {
    isEdit, renderItems, settings, onUpdate, formRef,
    onCancel, loading,
  } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const rules = FORM_RULES(t);
  const [updatedInitialValues, setUpdatedInitialValues] = useState<any>({});
  const [radioOption, setRadioOption] = useState<string>('block');
  const [localBlocks, setLocalBlocks] = useState<any>([]);
  const [localRanchIds, setLocalRanchIds] = useState<any>([]);
  const [showRanchBlock, setShowRanchBlock] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const { renderButton, renderInputFormItem } = useRenderReusableControls(t);
  const blockNames = useBlockNames(localBlocks) || {};
  const ranchNames = useRanchNames(localRanchIds) || {};
  const blockIdsForRanch = useBlockIdsForRanchIds() || {};
  const { onGlobalSettingsFinish } = useHandlers(updatedInitialValues, isEdit, onUpdate);

  const isBlockOption = radioOption === 'block';
  // To update the Form with initial value
  const autoFillValues = settings;

  /* eslint-disable no-template-curly-in-string */
  const validateMessages = {
    required: t('${label} is required!'),
  };
  const pressureFormat = useSelector(selectUserPressureFormat);
  const pressureLabel = pressureFormat === 'pounds_per_square_inch' ? 'psi' : 'mpa';
  const { requiredPositiveNumberField, requiredField } = rules;

  useUpdateGlobalSettingsFormFields(autoFillValues, form,
    setSelectedSensor, setLocalBlocks, setUpdatedInitialValues);

  const onDelete = () => {
    setShowModal(true);
  };

  useEffect(() => {
    if (formRef) formRef(form);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  
  useEffect(() => {
    if (autoFillValues) {
      const { reservoir_threshold } = autoFillValues;
      form.setFieldsValue({ blocks: reservoir_threshold?.blocks });
    }
  }, [autoFillValues]);

  const handleAddBlock = () => {
    setShowRanchBlock(true);
  };

  const onCustomFinish = (d) => {
    const blockIds = localRanchIds?.map((id) => blockIdsForRanch[id]).flat()
    const finalObj = {
      ...(settings?.id ? { id: settings?.id } : {}),
      ...d,
      blocks: isBlockOption ? localBlocks : blockIds,
    };
    onGlobalSettingsFinish(finalObj);
  }

  const onRaioChange = (d) => {
    const radioValue = d?.target?.value;
    setRadioOption(radioValue);
  }

  const onBlockSelect = (d) => {
    if (!localBlocks?.includes(d)) {
      const updatedBlocks = [...localBlocks, d];
      setLocalBlocks(updatedBlocks);
      setShowRanchBlock(false);
      form.setFieldsValue({ blocks: updatedBlocks });
    } else if (localBlocks?.includes(d)) {
      const filteredBlockIds = localBlocks.filter((block) => block !== d);
      setLocalBlocks(filteredBlockIds);
      form.setFieldsValue({ blocks: filteredBlockIds });
    }
  }
  const onRanchSelect = (d) => {
    if (!localRanchIds?.includes(d)) {
      const updatedRanchIds = [...localBlocks, d];
      setLocalRanchIds([...localRanchIds, d])
      const blockIds = updatedRanchIds?.map((id) => blockIdsForRanch[id]).flat()
      form.setFieldsValue({ blocks: blockIds });
      setShowRanchBlock(false);
    } else if (localRanchIds?.includes(d)) {
      const filteredRanchIds = localRanchIds.filter((ranch) => ranch !== d);
      const blockIds = filteredRanchIds?.map((id) => blockIdsForRanch[id]).flat()
      setLocalRanchIds(filteredRanchIds);
      form.setFieldsValue({ blocks: blockIds });
    }
  }

  const onChangeSelect = (d) => {
    setShowRanchBlock(false);
  }

  return (
    <div className={`rule-setting-form ${isMobile ? 'width-100-percent' : ''} margin-inherit`}>
      <Form
        form={form}
        layout="vertical"
        name="global Settings"
        onFinish={(d) => onCustomFinish(d)}
        validateMessages={validateMessages}
        initialValues={updatedInitialValues}
      >
      <SensorControl
        form={form}
        setSelectedSensor={setSelectedSensor}
        sensor={selectedSensor}
        formLabel={t("Main Pressure Sensor")}
        sensorTypes={capabilities?.water_pressure}
      />
          {renderInputFormItem('irrigation_pressure_high', t('Irrigation Re-starting Pressure Cut Off'),
            pressureLabel, 'label-width font-bold', requiredPositiveNumberField, 'High')}
          {renderInputFormItem('irrigation_pressure_low', t('Irrigation Stopping Pressure Cut Off'),
            pressureLabel, 'label-width font-bold', requiredPositiveNumberField, 'Low')}

        <Form.Item
          label={t("Select Associated")}
          name="association"
          className="font-bold form-item-width-100"
        >
          <Radio.Group
            onChange={onRaioChange}
            defaultValue={radioOption}
            className="radio-group"
          >
            <Radio className="radio-text-normal" value="ranch">{t('Ranches')}</Radio>
            <Radio className="radio-text-normal" value="block">{t('Blocks')}</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={t("Associated Blocks")}
          name="blocks"
          className="font-bold form-item-width-100"
          rules={requiredField}
        >
          <Space direction="vertical" style={{ display: "flex" }}>
            <div className='block-list'>
              <div className='block-list-inner'>
                {isBlockOption && localBlocks.map((block, index) => (
                  <Tag key={`${index}_${block}`}
                  className='block-tag'
                  closable onClose={() => onBlockSelect(block)}>
                    {blockNames?.[block]}
                  </Tag>
                ))}
                {!isBlockOption && localRanchIds.map((ranch, index) => (
                  <Tag key={`${index}_${ranch}`}
                  className='block-tag'
                  closable onClose={() => onRanchSelect(ranch)}>
                    {ranchNames?.[ranch]}
                  </Tag>
                ))}
                {isBlockOption && !localBlocks?.length && (
                  <span className="radio-text-normal no-data-text">{t('No Blocks')}</span>
                )}
                {!isBlockOption && !localRanchIds?.length && (
                  <span className="radio-text-normal no-data-text">
                    {t('No Blocks For Ranch')}
                  </span>
                )}
              </div>
            </div>
            {showRanchBlock && isBlockOption &&
            <div className='flex-space-between margin-15'>
              <BlockSelect onSelect={onBlockSelect} onChange={onChangeSelect} />
              <CloseOutlined onClick={onChangeSelect}/>
            </div>}
            {showRanchBlock && !isBlockOption &&
            <div className='flex-space-between margin-15'>
              <RanchSelect onSelect={onRanchSelect} onChange={onChangeSelect} />
              <CloseOutlined onClick={onChangeSelect}/>
            </div>}
            {!showRanchBlock && renderButton(isBlockOption
              ? 'Add Block' : 'Add Ranch', 'dashed', undefined, handleAddBlock,
              false, undefined, false, 'add-block-btn')}
          </Space>
        </Form.Item>
    
        {/* Button Controls  */}
        {!isEdit && (
          <div className="flex-space-between margin-15">
            <Form.Item className="font-bold form-item-width-100">
              {renderButton('Save', 'primary', 'submit', undefined, false, undefined, loading)}
            </Form.Item>
            <Form.Item className="font-bold form-item-width-100">
              {renderButton('Cancel', undefined, undefined, onCancel, loading)}
            </Form.Item>
          </div>
        )}
        {isEdit && (
        <div className="flex-space-between margin-15">
          <Form.Item className="font-bold form-item-width-100">
            {renderButton('Update', 'primary', 'submit', undefined, false, undefined, loading)}
          </Form.Item>
          <Form.Item className="font-bold form-item-width-100">
            {renderButton('Delete', undefined, undefined, onDelete, loading)}
          </Form.Item>
        </div>
        )}
      </Form>
      <ConfirmDeleteModal
        globalConfigId={settings?.id}
        showModal={showModal}
        setShowModal={setShowModal}
        onUpdate={onUpdate}
      />
    </div>
  );
};

GlobalSettingsConfig.defaultProps = {
  isEdit: false,
};

export default GlobalSettingsConfig;
